import { Component, ElementRef, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { ConnectedPosition } from '@angular/cdk/overlay';
import { ROW_ACTIONS, RowActionName, RowActionVariant } from '../../design/row-actions';

export interface MenuAction {
  id: string;
  label: string;
  action?: RowActionName;
  icon?: string;
  tone?: RowActionVariant;
  disabled?: boolean;
  separator?: boolean;
}

const MENU_OFFSET = 6;

@Component({
  selector: 'ss-action-menu',
  templateUrl: './action-menu.component.html',
  standalone: false
})
export class ActionMenuComponent {
  @Input() items: MenuAction[] = [];
  @Input() label = '';
  @Input() title = 'Mais ações';
  @Input() icon = 'ellipsis';
  @Input() disabled = false;
  @Input() hideTrigger = false;
  @Output() actionSelected = new EventEmitter<string>();

  @ViewChild('trigger') trigger?: ElementRef<HTMLButtonElement>;
  @ViewChild('panel') panel?: ElementRef<HTMLElement>;

  opened = false;
  anchor?: ElementRef<HTMLElement>;

  readonly positions: ConnectedPosition[] = [
    { originX: 'end', originY: 'bottom', overlayX: 'end', overlayY: 'top', offsetY: MENU_OFFSET },
    { originX: 'end', originY: 'top', overlayX: 'end', overlayY: 'bottom', offsetY: -MENU_OFFSET },
    { originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top', offsetY: MENU_OFFSET },
    { originX: 'start', originY: 'top', overlayX: 'start', overlayY: 'bottom', offsetY: -MENU_OFFSET }
  ];

  definition(item: MenuAction) {
    return item.action ? ROW_ACTIONS[item.action] : ROW_ACTIONS.more;
  }

  tone(item: MenuAction): RowActionVariant {
    return item.tone || this.definition(item).variant;
  }

  toggle() {
    if (this.opened) {
      this.close();
    } else {
      this.opened = true;
    }
  }

  openAt(element: HTMLElement) {
    this.anchor = new ElementRef(element);
    this.opened = true;
  }

  close(restoreFocus = true) {
    this.opened = false;
    if (restoreFocus) (this.anchor?.nativeElement || this.trigger?.nativeElement)?.focus();
  }

  select(item: MenuAction) {
    if (item.disabled) return;
    this.actionSelected.emit(item.id);
    this.close();
  }

  focusFirst() {
    setTimeout(() => this.panel?.nativeElement.querySelector<HTMLButtonElement>('button:not(:disabled)')?.focus());
  }

  keydown(event: KeyboardEvent) {
    if (event.key === 'Escape') {
      event.preventDefault();
      this.close();
      return;
    }
    if (event.key === 'Tab') {
      this.close(false);
      return;
    }
    if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) return;

    event.preventDefault();
    const buttons = Array.from(this.panel!.nativeElement.querySelectorAll<HTMLButtonElement>('button:not(:disabled)'));
    const current = buttons.indexOf(event.target as HTMLButtonElement);
    let next = current + (event.key === 'ArrowDown' ? 1 : -1);
    if (event.key === 'Home') next = 0;
    if (event.key === 'End') next = buttons.length - 1;
    buttons[(next + buttons.length) % buttons.length]?.focus();
  }
}
